import React, { useState, useEffect, useCallback } from 'react';
import { FolderOpen, Send, Upload, FileText, Trash2, CheckCircle, Clock, Eye, UploadCloud, Mail, MessageSquare, X, Loader } from 'lucide-react';

// Engine · Documents page — "Driver Documents" panel.
// Push a document to a specific driver's portal "My Docs" tab, filed under a
// category. Two ways in: compose a letter (rendered to a branded PDF server-side)
// or upload a finished PDF. The assignments table shows the read-receipt — when
// the driver opened each doc (date + time).

const API = '/.netlify/functions/driver-docs-sb';
const CATEGORIES = ['Operations', 'Safety', 'Briefings', 'Alerts', 'Personal', 'Contracts'];

function fmtWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function b64ToBlob(base64, type) {
  const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  return new Blob([bytes], { type: type || 'application/pdf' });
}

export default function DriverDocsManager({ drivers = [], addNotification }) {
  const [mode, setMode]           = useState('letter');   // 'letter' | 'upload' | 'request'
  const [driverLogin, setDriver]  = useState('');
  const [driverFilter, setFilter] = useState('');
  const [category, setCategory]   = useState('Personal');
  const [title, setTitle]         = useState('');
  const [body, setBody]           = useState('');
  const [note, setNote]           = useState('');         // request mode: extra instructions
  const [file, setFile]           = useState(null);       // { base64, filename, contentType }
  const [requiresAck, setAck]     = useState(false);      // driver must tap "Acknowledge"
  // Optional home-screen banner on the driver's portal, from..to inclusive.
  const [banner, setBanner]       = useState({ on: false, from: '', to: '', text: '' });
  const [busy, setBusy]           = useState(false);
  const [rows, setRows]           = useState([]);
  const [reqRows, setReqRows]     = useState([]);         // outstanding upload requests
  const [preview, setPreview]     = useState(null);       // request preview (before send)

  const api = useCallback(async (payload) => {
    const res = await fetch(API, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  }, []);

  const loadRows = useCallback(async () => {
    const d = await api({ action: 'listAssignments' });
    if (d && d.success) setRows(d.documents || []);
  }, [api]);

  const loadReqRows = useCallback(async () => {
    const d = await api({ action: 'listUploadRequests' });
    if (d && d.success) setReqRows(d.requests || []);
  }, [api]);

  useEffect(() => { loadRows(); loadReqRows(); }, [loadRows, loadReqRows]);

  // ── Request-a-document flow: preview the email + SMS, then send ──────────────
  const resolveDriver = () => (driverLogin || driverFilter).trim().toUpperCase();

  const doPreview = async () => {
    const drv = resolveDriver();
    if (!drv) { addNotification?.('Pick a driver', 'error'); return; }
    if (!title.trim()) { addNotification?.('Name the document you need', 'error'); return; }
    setBusy(true);
    try {
      const d = await api({ action: 'previewUploadRequest', driverLogin: drv, title: title.trim(), note: note.trim() });
      if (d && d.success) setPreview({ ...d, driverLogin: drv });
      else addNotification?.(d?.error || 'Could not build preview', 'error');
    } catch (e) { addNotification?.(e.message, 'error'); } finally { setBusy(false); }
  };

  const sendRequest = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const d = await api({ action: 'requestUpload', driverLogin: preview.driverLogin, title: title.trim(), note: note.trim(), category });
      if (d && d.success) {
        const bits = [d.sent?.email ? 'email' : '', d.sent?.sms ? 'SMS' : ''].filter(Boolean).join(' + ');
        addNotification?.(`Requested “${title.trim()}” from ${preview.driverLogin}${bits ? ` (${bits} sent)` : ' (portal only — no email/SMS on file)'}`, 'success');
        setTitle(''); setNote(''); setPreview(null);
        loadReqRows();
      } else addNotification?.(d?.error || 'Could not send request', 'error');
    } catch (e) { addNotification?.(e.message, 'error'); } finally { setBusy(false); }
  };

  const cancelRequest = async (id) => {
    const d = await api({ action: 'cancelUploadRequest', id });
    if (d && d.success) { setReqRows(reqRows.filter(r => r.id !== id)); addNotification?.('Request cancelled', 'success'); }
  };

  const filtered = (drivers || []).filter(d =>
    !driverFilter ||
    d.name?.toLowerCase().includes(driverFilter.toLowerCase()) ||
    d.loginName?.toLowerCase().includes(driverFilter.toLowerCase())
  ).slice(0, 8);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    const reader = new FileReader();
    reader.onload = () => setFile({ base64: String(reader.result), filename: f.name, contentType: f.type || 'application/pdf' });
    reader.readAsDataURL(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  };

  const submit = async () => {
    const drv = (driverLogin || driverFilter).trim().toUpperCase();
    if (!drv) { addNotification?.('Pick a driver', 'error'); return; }
    if (!title.trim()) { addNotification?.('Give the document a title', 'error'); return; }
    if (mode === 'letter' && !body.trim()) { addNotification?.('Write the letter text', 'error'); return; }
    if (mode === 'upload' && !file) { addNotification?.('Choose a PDF to upload', 'error'); return; }

    setBusy(true);
    try {
      if (banner.on && (!banner.from || !banner.to || banner.from > banner.to)) {
        addNotification?.('Home banner needs a from and to date', 'error'); setBusy(false); return;
      }
      const homeBanner = banner.on ? { from: banner.from, to: banner.to, text: banner.text } : null;
      const payload = mode === 'letter'
        ? { action: 'assignLetter', driverLogin: drv, category, title: title.trim(), bodyText: body, requiresAck, homeBanner }
        : { action: 'assignDocument', driverLogin: drv, category, title: title.trim(), fileBase64: file.base64, filename: file.filename, contentType: file.contentType, requiresAck, homeBanner };
      const d = await api(payload);
      if (d && d.success) {
        addNotification?.(`Sent “${title.trim()}” to ${drv}`, 'success');
        setTitle(''); setBody(''); setFile(null); setAck(false); setBanner({ on: false, from: '', to: '', text: '' });
        loadRows();
      } else {
        addNotification?.(d?.error || 'Could not send', 'error');
      }
    } catch (e) { addNotification?.(e.message, 'error'); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    const d = await api({ action: 'deleteAssignment', id });
    if (d && d.success) { setRows(rows.filter(r => r.id !== id)); addNotification?.('Document removed', 'success'); }
    else addNotification?.(d?.error || 'Could not remove', 'error');
  };

  // View the letter/PDF. Opens the tab synchronously (before the await) so popup
  // blockers don't swallow it. This is a preview — it does NOT mark the doc opened.
  const [viewing, setViewing] = useState('');
  const view = async (r, ackCopy = false) => {
    const w = window.open('', '_blank');
    setViewing(r.id);
    try {
      const d = await api({ action: ackCopy ? 'getAckFile' : 'getAssignmentFile', id: r.id });
      if (!d || !d.success) { if (w) w.close(); addNotification?.(d?.error || 'Could not open document', 'error'); return; }
      const url = URL.createObjectURL(b64ToBlob(d.base64, d.contentType));
      if (w) w.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (w) w.close(); addNotification?.(e.message, 'error'); } finally { setViewing(''); }
  };

  const inp = { width: '100%', padding: '9px 11px', fontSize: '0.85rem', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-bg)', color: 'var(--hp-text)' };
  const lbl = { fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--hp-dim)', marginBottom: 5, display: 'block' };

  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div style={{ fontWeight: 700, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <FolderOpen size={16} color="var(--hp-gold)" /> Driver Documents — push to a driver’s “My Docs”
      </div>
      <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 16 }}>
        Sends a document to one driver’s portal. A red dot appears until they open it; you’ll see the open time below.
      </div>

      {/* Mode toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[{ k: 'letter', icon: <FileText size={13} />, label: 'Compose letter' }, { k: 'upload', icon: <Upload size={13} />, label: 'Upload PDF' }, { k: 'request', icon: <UploadCloud size={13} />, label: 'Request a document' }].map(m => (
          <button key={m.k} onClick={() => setMode(m.k)}
            className={`btn btn-sm ${mode === m.k ? 'btn-primary' : 'btn-secondary'}`}
            style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {m.icon} {m.label}
          </button>
        ))}
      </div>

      <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        {/* Driver */}
        <div>
          <label style={lbl}>Driver</label>
          <input style={inp} placeholder="Search name or login…" value={driverFilter}
            onChange={e => { setFilter(e.target.value); setDriver(''); }} />
          {driverFilter && !driverLogin && (
            <div style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', marginTop: 4, maxHeight: 160, overflow: 'auto' }}>
              {filtered.length === 0 ? (
                <div style={{ padding: 8, fontSize: '0.78rem', color: 'var(--hp-dim)' }}>
                  No match — will send to “{driverFilter.toUpperCase()}”
                </div>
              ) : filtered.map(d => (
                <div key={d.loginName} onClick={() => { setDriver(d.loginName); setFilter(`${d.name || d.loginName} (${d.loginName})`); }}
                  style={{ padding: '7px 10px', fontSize: '0.8rem', cursor: 'pointer', borderBottom: '1px solid var(--hp-border)' }}>
                  <strong>{d.name || d.loginName}</strong> <span style={{ color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>{d.loginName}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Category */}
        <div>
          <label style={lbl}>Category</label>
          <select style={inp} value={category} onChange={e => setCategory(e.target.value)}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={lbl}>{mode === 'request' ? 'Document requested' : 'Title'}</label>
        <input style={inp} placeholder={mode === 'request' ? 'e.g. Updated insurance certificate' : 'e.g. College attendance letter'} value={title} onChange={e => setTitle(e.target.value)} />
      </div>

      {mode === 'letter' && (
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Letter text <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— rendered on a Hollypaul letterhead</span></label>
          <textarea style={{ ...inp, minHeight: 200, fontFamily: 'var(--font-body)', lineHeight: 1.5, resize: 'vertical' }}
            placeholder={'8 July 2026\n\nTo Whom It May Concern\n\n…'}
            value={body} onChange={e => setBody(e.target.value)} />
        </div>
      )}
      {mode === 'upload' && (
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>PDF file</label>
          <input type="file" accept="application/pdf,.pdf" onChange={onFile} style={{ fontSize: '0.82rem' }} />
          {file && <div style={{ fontSize: '0.75rem', color: 'var(--hp-green)', marginTop: 6 }}>✓ {file.filename}</div>}
        </div>
      )}
      {mode === 'request' && (
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Note to the contractor <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— optional, appears in the email + SMS</span></label>
          <textarea style={{ ...inp, minHeight: 80, fontFamily: 'var(--font-body)', lineHeight: 1.5, resize: 'vertical' }}
            placeholder="e.g. Your current one expires next week — please send the renewed certificate."
            value={note} onChange={e => setNote(e.target.value)} />
        </div>
      )}

      {mode === 'request' ? (
        <button className="btn btn-primary" onClick={doPreview} disabled={busy}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <UploadCloud size={13} /> {busy ? 'Working…' : 'Preview request'}
        </button>
      ) : (<>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.8rem', marginBottom: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={requiresAck} onChange={e => setAck(e.target.checked)} style={{ marginTop: 2 }} />
          <span><strong>Requires acknowledgement</strong> — the driver taps “Acknowledge” in their portal; a stamped copy is emailed to you and kept here.</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.8rem', marginBottom: banner.on ? 8 : 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={banner.on} onChange={e => setBanner(b => ({ ...b, on: e.target.checked }))} style={{ marginTop: 2 }} />
          <span><strong>Show on the driver’s home screen</strong> — a banner on their portal between two dates (inclusive), linking to this document.</span>
        </label>
        {banner.on && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 12 }}>
            <div><label style={lbl}>From</label><input type="date" style={inp} value={banner.from} onChange={e => setBanner(b => ({ ...b, from: e.target.value }))} /></div>
            <div><label style={lbl}>To</label><input type="date" style={inp} value={banner.to} onChange={e => setBanner(b => ({ ...b, to: e.target.value }))} /></div>
            <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Banner text (optional — defaults to the title)</label>
              <input style={inp} placeholder="One or two lines the driver sees on the home screen" value={banner.text} onChange={e => setBanner(b => ({ ...b, text: e.target.value }))} /></div>
          </div>
        )}
        <button className="btn btn-primary" onClick={submit} disabled={busy}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Send size={13} /> {busy ? 'Sending…' : 'Send to driver'}
        </button>
      </>)}

      {/* Outstanding upload requests */}
      {reqRows.filter(r => r.status === 'pending').length > 0 && (
        <div style={{ marginTop: 22 }}>
          <div style={{ ...lbl, marginBottom: 8 }}>Awaiting upload · {reqRows.filter(r => r.status === 'pending').length}</div>
          <div style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
            {reqRows.filter(r => r.status === 'pending').map(r => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid var(--hp-border)', fontSize: '0.8rem' }}>
                <UploadCloud size={14} color="var(--hp-gold)" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</div>
                  <div style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}>
                    <span style={{ fontFamily: 'var(--font-mono)' }}>{r.driverLogin}</span> · requested {fmtWhen(r.requestedAt)}
                  </div>
                </div>
                <button onClick={() => cancelRequest(r.id)} title="Cancel request"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-dim)', display: 'flex' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Assignments / read receipts */}
      {rows.length > 0 && (
        <div style={{ marginTop: 22 }}>
          <div style={{ ...lbl, marginBottom: 8 }}>Sent documents · {rows.length}</div>
          <div style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
            {rows.map(r => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '1px solid var(--hp-border)', fontSize: '0.8rem' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.title}</div>
                  <div style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}>
                    <span style={{ fontFamily: 'var(--font-mono)' }}>{r.driverLogin}</span> · {r.category} · sent {fmtWhen(r.assignedAt)}
                    {r.homeBanner && <> · home banner {r.homeBanner.from} to {r.homeBanner.to}</>}
                  </div>
                </div>
                {r.requiresAck && (r.acknowledgedAt ? (
                  <button onClick={() => view(r, true)} disabled={viewing === r.id} title="View the stamped acknowledgement copy"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 9px', fontSize: '0.75rem', fontWeight: 700, border: '1px solid var(--hp-green)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)', color: 'var(--hp-green)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <CheckCircle size={13} /> Acknowledged {fmtWhen(r.acknowledgedAt)}
                  </button>
                ) : (
                  <span style={{ color: 'var(--hp-gold)', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', fontWeight: 700 }}>
                    <Clock size={13} /> Awaiting acknowledgement
                  </span>
                ))}
                {r.status === 'opened' ? (
                  <span style={{ color: 'var(--hp-green)', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                    <CheckCircle size={13} /> Opened {fmtWhen(r.openedAt)}
                  </span>
                ) : (
                  <span style={{ color: 'var(--hp-gold)', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                    <Clock size={13} /> Unopened
                  </span>
                )}
                <button onClick={() => view(r)} disabled={viewing === r.id} title="View"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 9px', fontSize: '0.75rem', fontWeight: 700, border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)', color: 'var(--hp-text)', cursor: 'pointer', opacity: viewing === r.id ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                  <Eye size={13} /> {viewing === r.id ? '…' : 'View'}
                </button>
                <button onClick={() => remove(r.id)} title="Remove"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-dim)', display: 'flex' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Request preview — confirm the email + SMS before it goes out */}
      {preview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1300, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '24px 12px', overflowY: 'auto' }}>
          <div style={{ width: 560, maxWidth: '100%', background: 'var(--hp-card)', color: 'var(--hp-text)', borderRadius: 14, border: '1px solid var(--hp-border)', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid var(--hp-border)' }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Request “{title.trim()}” from {preview.driver?.name || preview.driverLogin}</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setPreview(null)} disabled={busy}><X size={16} /></button>
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {preview.warnings?.length > 0 && (
                <div style={{ fontSize: '0.8rem', color: 'var(--hp-red)', background: 'rgba(239,68,68,0.08)', border: '1px solid var(--hp-red)', borderRadius: 8, padding: '10px 12px' }}>
                  {preview.warnings.join(' ')}
                </div>
              )}
              <div>
                <div style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 6 }}><Mail size={13} /> Email {preview.email?.to ? `→ ${preview.email.to}` : '— none on file (skipped)'}</div>
                {preview.email?.to && (
                  <div style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 12, fontSize: '0.82rem', background: 'var(--hp-bg)' }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>{preview.email.subject}</div>
                    <div dangerouslySetInnerHTML={{ __html: preview.email.html }} />
                  </div>
                )}
              </div>
              <div>
                <div style={{ ...lbl, display: 'flex', alignItems: 'center', gap: 6 }}><MessageSquare size={13} /> SMS {preview.sms?.to ? `→ ${preview.sms.to}` : '— no mobile on file (skipped)'}</div>
                {preview.sms?.to && (
                  <div style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 12, fontSize: '0.82rem', background: 'var(--hp-bg)', whiteSpace: 'pre-wrap' }}>
                    {preview.sms.body}
                  </div>
                )}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)' }}>
                A red banner also appears on the contractor’s My Shifts page prompting them to upload.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, padding: '16px 20px', borderTop: '1px solid var(--hp-border)' }}>
              <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} disabled={busy} onClick={sendRequest}>
                {busy ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Sending…</> : <><Send size={14} /> Send request</>}
              </button>
              <button className="btn btn-secondary" onClick={() => setPreview(null)} disabled={busy}>Back</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
